import React from 'react';
import { useSelector } from 'react-redux';
import { getAssetDimensions, getAssetsComposition } from '../../finder/selectors';
import { getViewMode } from '../../viewMode/selectors';
import { ViewMode } from '../../viewMode/model';
import { SkeletonBlock } from './Skeleton';
import {
  ASSET_CARD_PADDING,
  ASSET_IMAGE_SLOT_HEIGHT,
  CREATIVE_IMAGE_SLOT_HEIGHT,
  SCROLLBAR_WIDTH,
} from '../../finder/model';
import '../styles.scss';

// Длина плашки-названия чередуется, чтобы сетка не выглядела штампованной.
const nameRatioAt = (index: number) => (index % 3 ? 0.8 : 0.55);

type CardMetrics = { assetWidth: number; assetHeight: number; imageHeight: number };

function useCardMetrics(): CardMetrics {
  const { assetWidth, assetHeight } = useSelector(getAssetDimensions);
  const isCreative = useSelector(getViewMode) === ViewMode.creatives;
  return {
    assetWidth,
    assetHeight,
    imageHeight: isCreative ? CREATIVE_IMAGE_SLOT_HEIGHT : ASSET_IMAGE_SLOT_HEIGHT,
  };
}

const AssetCardSkeleton: React.FC<CardMetrics & { nameRatio: number; muted?: boolean }> = ({
  assetWidth,
  assetHeight,
  imageHeight,
  nameRatio,
  muted,
}) => {
  const cardWidth = assetWidth - ASSET_CARD_PADDING * 2;
  return (
    <div
      className={
        muted ? 'al-skeleton-grid__cell al-skeleton-grid__cell_muted' : 'al-skeleton-grid__cell'
      }
      style={{ width: assetWidth, height: assetHeight }}
      aria-hidden
    >
      <SkeletonBlock
        className="al-skeleton-grid__image"
        style={{ height: imageHeight, width: cardWidth }}
      />
      <SkeletonBlock className="al-skeleton-grid__name" style={{ width: cardWidth * nameRatio }} />
    </div>
  );
};

// Хвост частично заполненного ряда. Страница с бэка почти никогда не кратна числу
// колонок, поэтому последний ряд реальных карточек обрывается на середине. Если
// впереди есть ещё страница, добиваем его призраками — иначе между ним и рядом-
// заглушкой зияет дыра на несколько миниатюр.
export const AssetRowFillSkeleton: React.FC<{ count: number; startIndex: number }> = ({
  count,
  startIndex,
}) => {
  const metrics = useCardMetrics();

  if (count < 1) {
    return null;
  }

  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <AssetCardSkeleton
          key={index}
          {...metrics}
          nameRatio={nameRatioAt(startIndex + index)}
          muted
        />
      ))}
    </>
  );
};

// Один ряд карточек-призраков. Используется и в скелетоне первой загрузки, и как
// заглушка ещё не подгруженной страницы в самом списке (там с muted и подписью).
// Подпись лежит поверх карточек и в приглушение не попадает — иначе текст был бы
// нечитаемым вместе с ними.
export const AssetRowSkeleton: React.FC<{ perRow: number; muted?: boolean; label?: string }> = ({
  perRow,
  muted,
  label,
}) => {
  const metrics = useCardMetrics();
  const { gapVertical } = useSelector(getAssetsComposition);

  if (perRow < 1) {
    return null;
  }

  return (
    <div
      className={
        muted ? 'al-skeleton-grid__row al-skeleton-grid__row_muted' : 'al-skeleton-grid__row'
      }
      style={{ height: metrics.assetHeight + gapVertical }}
    >
      {Array.from({ length: perRow }, (_, index) => (
        <AssetCardSkeleton key={index} {...metrics} nameRatio={nameRatioAt(index)} muted={muted} />
      ))}

      {label ? (
        <div className="al-skeleton-grid__label">
          <span className="al-loading-pill" role="status">
            {label}
          </span>
        </div>
      ) : null}
    </div>
  );
};

// Скелетон сетки ассетов. Размеры карточек и зазоры берём из тех же источников, что и
// настоящий список, а число колонок считаем по фактической ширине контейнера, а не по
// containerWidth из стора: на холодном старте тот ещё нулевой (его выставляет AssetList,
// которого сейчас на экране нет).
export const AssetGridSkeleton: React.FC<{ width: number; height: number }> = ({
  width,
  height,
}) => {
  const { assetWidth, assetHeight } = useSelector(getAssetDimensions);
  const { gapHorizontal, gapVertical } = useSelector(getAssetsComposition);

  const perRow = Math.floor(Math.max(width - SCROLLBAR_WIDTH, 0) / (assetWidth + gapHorizontal));
  if (perRow < 1 || height < 1) {
    return null;
  }

  const rows = Math.ceil(height / (assetHeight + gapVertical));

  return (
    <div className="al-skeleton-grid" style={{ width, height }} aria-hidden>
      {Array.from({ length: rows }, (_, rowIndex) => (
        <AssetRowSkeleton key={rowIndex} perRow={perRow} />
      ))}
    </div>
  );
};
